import React, { useState, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  RoleDefinition,
  RoleAssignment,
  RoleTemplate,
  RoleConflict,
  Permission,
  SystemRole,
  ProjectRole,
  ComponentRole,
  ConflictResolutionStrategy,
  PermissionScope,
  ResourceType,
  PermissionAction,
} from "@/types/permissions";
import { useAuthStore } from "@/stores/auth/auth-store";
import { config } from "@/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Plus,
  Edit,
  Trash,
  Users,
  Shield,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  Copy,
  Download,
  Upload,
  UserPlus,
  UserMinus,
  Layers,
  GitBranch,
  AlertCircle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Search,
  Filter,
  Settings,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface RoleManagementProps {
  organizationId?: string;
  projectId?: string;
  onRoleChange?: (role: RoleDefinition) => void;
}

export const RoleManagement: React.FC<RoleManagementProps> = ({
  organizationId,
  projectId,
  onRoleChange,
}) => {
  const { tokens } = useAuthStore();
  const queryClient = useQueryClient();

  // State
  const [selectedRole, setSelectedRole] = useState<RoleDefinition | null>(null);
  const [expandedRoles, setExpandedRoles] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState<
    "all" | "system" | "project" | "custom"
  >("all");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [conflicts, setConflicts] = useState<RoleConflict[]>([]);

  // API functions
  const roleApi = {
    fetchRoles: async () => {
      const params = new URLSearchParams();
      if (organizationId) params.append("organizationId", organizationId);
      if (projectId) params.append("projectId", projectId);

      const response = await fetch(`${config.api.baseUrl}/roles?${params}`, {
        headers: {
          Authorization: `Bearer ${tokens?.accessToken}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch roles");
      return response.json();
    },

    createRole: async (role: Partial<RoleDefinition>) => {
      const response = await fetch(`${config.api.baseUrl}/roles`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tokens?.accessToken}`,
        },
        body: JSON.stringify(role),
      });

      if (!response.ok) throw new Error("Failed to create role");
      return response.json();
    },

    updateRole: async (roleId: string, updates: Partial<RoleDefinition>) => {
      const response = await fetch(`${config.api.baseUrl}/roles/${roleId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tokens?.accessToken}`,
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) throw new Error("Failed to update role");
      return response.json();
    },

    deleteRole: async (roleId: string) => {
      const response = await fetch(`${config.api.baseUrl}/roles/${roleId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${tokens?.accessToken}`,
        },
      });

      if (!response.ok) throw new Error("Failed to delete role");
    },

    detectConflicts: async (roleIds: string[]) => {
      const response = await fetch(`${config.api.baseUrl}/roles/conflicts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tokens?.accessToken}`,
        },
        body: JSON.stringify({ roleIds }),
      });

      if (!response.ok) throw new Error("Failed to detect conflicts");
      return response.json();
    },

    resolveConflicts: async (
      conflictId: string,
      strategy: ConflictResolutionStrategy,
    ) => {
      const response = await fetch(
        `${config.api.baseUrl}/roles/conflicts/${conflictId}/resolve`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokens?.accessToken}`,
          },
          body: JSON.stringify({ strategy }),
        },
      );

      if (!response.ok) throw new Error("Failed to resolve conflicts");
      return response.json();
    },
  };

  // Queries
  const { data: roles = [], isLoading } = useQuery({
    queryKey: ["roles", organizationId, projectId],
    queryFn: roleApi.fetchRoles,
  });

  // Mutations
  const createRoleMutation = useMutation({
    mutationFn: roleApi.createRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      setShowCreateDialog(false);
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({
      roleId,
      updates,
    }: {
      roleId: string;
      updates: Partial<RoleDefinition>;
    }) => roleApi.updateRole(roleId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
  });

  const deleteRoleMutation = useMutation({
    mutationFn: roleApi.deleteRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      setSelectedRole(null);
    },
  });

  // Build role hierarchy
  const roleHierarchy = useCallback(() => {
    const hierarchy: Map<string, RoleDefinition[]> = new Map();

    roles.forEach((role: RoleDefinition) => {
      if (role.inheritsFrom && role.inheritsFrom.length > 0) {
        role.inheritsFrom.forEach((parentId) => {
          if (!hierarchy.has(parentId)) {
            hierarchy.set(parentId, []);
          }
          hierarchy.get(parentId)!.push(role);
        });
      }
    });

    return hierarchy;
  }, [roles]);

  // Filter roles
  const filteredRoles = roles.filter((role: RoleDefinition) => {
    const matchesSearch =
      role.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      role.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === "all" || role.type === filterType;
    return matchesSearch && matchesType;
  });

  // Check for conflicts when role selection changes
  useEffect(() => {
    if (
      selectedRole &&
      selectedRole.inheritsFrom &&
      selectedRole.inheritsFrom.length > 0
    ) {
      roleApi
        .detectConflicts([selectedRole.id, ...selectedRole.inheritsFrom])
        .then(setConflicts)
        .catch(console.error);
    }
  }, [selectedRole]);

  // Role tree component
  const RoleTreeItem = ({
    role,
    depth = 0,
  }: {
    role: RoleDefinition;
    depth?: number;
  }) => {
    const hierarchy = roleHierarchy();
    const children = hierarchy.get(role.id) || [];
    const isExpanded = expandedRoles.has(role.id);
    const hasChildren = children.length > 0;
    const isSelected = selectedRole?.id === role.id;

    return (
      <div>
        <div
          className={cn(
            "flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-accent",
            isSelected && "bg-accent",
            depth > 0 && "ml-4",
          )}
          onClick={() => setSelectedRole(role)}
        >
          {hasChildren && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setExpandedRoles((prev) => {
                  const next = new Set(prev);
                  if (isExpanded) {
                    next.delete(role.id);
                  } else {
                    next.add(role.id);
                  }
                  return next;
                });
              }}
              className="p-0.5"
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          )}

          <Shield className="h-4 w-4 text-muted-foreground" />

          <div className="flex-1 flex items-center gap-2">
            <span className="text-sm font-medium">{role.name}</span>
            <Badge variant="outline" className="text-xs">
              {role.type}
            </Badge>
            {!role.isActive && (
              <Badge variant="secondary" className="text-xs">
                Inactive
              </Badge>
            )}
          </div>

          {role.permissions.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              {role.permissions.length} permissions
            </Badge>
          )}
        </div>

        {hasChildren && isExpanded && (
          <div className="mt-1">
            {children.map((child) => (
              <RoleTreeItem key={child.id} role={child} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Create role dialog
  const CreateRoleDialog = () => {
    const [newRole, setNewRole] = useState<Partial<RoleDefinition>>({
      name: "",
      description: "",
      type: "custom",
      permissions: [],
      inheritsFrom: [],
      isActive: true,
      isEditable: true,
      isDeletable: true,
    });

    const [selectedTemplate, setSelectedTemplate] = useState<string>("");

    // Role templates
    const templates: RoleTemplate[] = [
      {
        id: "designer-template",
        name: "Designer",
        description: "Full design capabilities with limited admin access",
        category: "Design",
        permissions: [
          {
            id: "1",
            action: PermissionAction.CREATE,
            resource: ResourceType.COMPONENT,
            scope: PermissionScope.PROJECT,
          },
          {
            id: "2",
            action: PermissionAction.UPDATE,
            resource: ResourceType.COMPONENT,
            scope: PermissionScope.PROJECT,
          },
        ],
        recommendedFor: ["Design teams", "UI/UX professionals"],
      },
      {
        id: "reviewer-template",
        name: "Reviewer",
        description: "View and comment on projects",
        category: "Collaboration",
        permissions: [
          {
            id: "3",
            action: PermissionAction.READ,
            resource: ResourceType.PROJECT,
            scope: PermissionScope.PROJECT,
          },
        ],
        recommendedFor: ["Stakeholders", "Clients"],
      },
    ];

    const handleTemplateSelect = (templateId: string) => {
      const template = templates.find((t) => t.id === templateId);
      if (template) {
        setNewRole((prev) => ({
          ...prev,
          name: template.name,
          description: template.description,
          permissions: template.permissions,
        }));
        setSelectedTemplate(templateId);
      }
    };

    return (
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New Role</DialogTitle>
            <DialogDescription>
              Define a new role with specific permissions and inheritance
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="details" className="mt-4">
            <TabsList>
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="templates">Templates</TabsTrigger>
              <TabsTrigger value="permissions">Permissions</TabsTrigger>
              <TabsTrigger value="inheritance">Inheritance</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-4">
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">Role Name</Label>
                  <Input
                    id="name"
                    value={newRole.name}
                    onChange={(e) =>
                      setNewRole((prev) => ({ ...prev, name: e.target.value }))
                    }
                    placeholder="Enter role name"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={newRole.description}
                    onChange={(e) =>
                      setNewRole((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    placeholder="Describe the role's purpose and responsibilities"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="type">Role Type</Label>
                  <Select
                    value={newRole.type}
                    onValueChange={(value) =>
                      setNewRole((prev) => ({ ...prev, type: value as any }))
                    }
                  >
                    <SelectTrigger id="type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="system">System</SelectItem>
                      <SelectItem value="project">Project</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="active"
                      checked={newRole.isActive}
                      onCheckedChange={(checked) =>
                        setNewRole((prev) => ({
                          ...prev,
                          isActive: checked as boolean,
                        }))
                      }
                    />
                    <Label htmlFor="active">Active</Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="editable"
                      checked={newRole.isEditable}
                      onCheckedChange={(checked) =>
                        setNewRole((prev) => ({
                          ...prev,
                          isEditable: checked as boolean,
                        }))
                      }
                    />
                    <Label htmlFor="editable">Editable</Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="deletable"
                      checked={newRole.isDeletable}
                      onCheckedChange={(checked) =>
                        setNewRole((prev) => ({
                          ...prev,
                          isDeletable: checked as boolean,
                        }))
                      }
                    />
                    <Label htmlFor="deletable">Deletable</Label>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="templates" className="space-y-4">
              <div className="grid gap-4">
                {templates.map((template) => (
                  <Card
                    key={template.id}
                    className={cn(
                      "cursor-pointer transition-all",
                      selectedTemplate === template.id && "ring-2 ring-primary",
                    )}
                    onClick={() => handleTemplateSelect(template.id)}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-base">
                            {template.name}
                          </CardTitle>
                          <CardDescription className="text-sm mt-1">
                            {template.description}
                          </CardDescription>
                        </div>
                        <Badge>{template.category}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="text-xs text-muted-foreground">
                        <p>
                          Recommended for: {template.recommendedFor.join(", ")}
                        </p>
                        <p className="mt-1">
                          {template.permissions.length} permissions included
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="permissions" className="space-y-4">
              <div className="space-y-4">
                <div className="grid gap-2">
                  <Label>Resource Permissions</Label>
                  <div className="border rounded-lg p-4 space-y-2 max-h-64 overflow-y-auto">
                    {Object.values(ResourceType).map((resource) => (
                      <div key={resource} className="space-y-2">
                        <div className="font-medium text-sm">{resource}</div>
                        <div className="grid grid-cols-4 gap-2 ml-4">
                          {Object.values(PermissionAction)
                            .slice(0, 4)
                            .map((action) => (
                              <div
                                key={`${resource}-${action}`}
                                className="flex items-center gap-1"
                              >
                                <Checkbox
                                  id={`${resource}-${action}`}
                                  checked={newRole.permissions?.some(
                                    (p) =>
                                      p.resource === resource &&
                                      p.action === action,
                                  )}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      setNewRole((prev) => ({
                                        ...prev,
                                        permissions: [
                                          ...(prev.permissions || []),
                                          {
                                            id: `${resource}-${action}`,
                                            action,
                                            resource,
                                            scope: PermissionScope.PROJECT,
                                          },
                                        ],
                                      }));
                                    } else {
                                      setNewRole((prev) => ({
                                        ...prev,
                                        permissions:
                                          prev.permissions?.filter(
                                            (p) =>
                                              !(
                                                p.resource === resource &&
                                                p.action === action
                                              ),
                                          ) || [],
                                      }));
                                    }
                                  }}
                                />
                                <Label
                                  htmlFor={`${resource}-${action}`}
                                  className="text-xs"
                                >
                                  {action}
                                </Label>
                              </div>
                            ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="inheritance" className="space-y-4">
              <div className="grid gap-2">
                <Label>Inherit Permissions From</Label>
                <div className="border rounded-lg p-4 space-y-2 max-h-64 overflow-y-auto">
                  {roles
                    .filter((r: RoleDefinition) => r.type !== "custom")
                    .map((role: RoleDefinition) => (
                      <div key={role.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`inherit-${role.id}`}
                          checked={newRole.inheritsFrom?.includes(role.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setNewRole((prev) => ({
                                ...prev,
                                inheritsFrom: [
                                  ...(prev.inheritsFrom || []),
                                  role.id,
                                ],
                              }));
                            } else {
                              setNewRole((prev) => ({
                                ...prev,
                                inheritsFrom:
                                  prev.inheritsFrom?.filter(
                                    (id) => id !== role.id,
                                  ) || [],
                              }));
                            }
                          }}
                        />
                        <Label
                          htmlFor={`inherit-${role.id}`}
                          className="flex items-center gap-2"
                        >
                          <Shield className="h-3 w-3" />
                          {role.name}
                          <Badge variant="outline" className="text-xs ml-2">
                            {role.permissions.length} permissions
                          </Badge>
                        </Label>
                      </div>
                    ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => createRoleMutation.mutate(newRole)}
              disabled={!newRole.name || createRoleMutation.isPending}
            >
              Create Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div className="flex h-full">
      {/* Left Sidebar - Role List */}
      <div className="w-80 border-r flex flex-col">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Roles</h3>
            <Button size="sm" onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-1" />
              New Role
            </Button>
          </div>

          <div className="relative mb-2">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search roles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8"
            />
          </div>

          <Select
            value={filterType}
            onValueChange={(value: any) => setFilterType(value)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="system">System</SelectItem>
              <SelectItem value="project">Project</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-2">
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">
                Loading roles...
              </div>
            ) : filteredRoles.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No roles found
              </div>
            ) : (
              <div className="space-y-1">
                {filteredRoles
                  .filter(
                    (role: RoleDefinition) =>
                      !role.inheritsFrom || role.inheritsFrom.length === 0,
                  )
                  .map((role: RoleDefinition) => (
                    <RoleTreeItem key={role.id} role={role} />
                  ))}
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Right Panel - Role Details */}
      <div className="flex-1 flex flex-col">
        {selectedRole ? (
          <>
            <div className="p-6 border-b">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-2xl font-semibold">
                    {selectedRole.name}
                  </h2>
                  {selectedRole.description && (
                    <p className="text-muted-foreground mt-1">
                      {selectedRole.description}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <Badge
                      variant={selectedRole.isActive ? "default" : "secondary"}
                    >
                      {selectedRole.isActive ? "Active" : "Inactive"}
                    </Badge>
                    <Badge variant="outline">{selectedRole.type}</Badge>
                    {selectedRole.permissions.length > 0 && (
                      <Badge variant="secondary">
                        {selectedRole.permissions.length} permissions
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {selectedRole.isEditable && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        /* Open edit dialog */
                      }}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                  )}
                  {selectedRole.isDeletable && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (
                          confirm(
                            `Are you sure you want to delete the role "${selectedRole.name}"?`,
                          )
                        ) {
                          deleteRoleMutation.mutate(selectedRole.id);
                        }
                      }}
                    >
                      <Trash className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <ScrollArea className="flex-1">
              <div className="p-6 space-y-6">
                {/* Inheritance Section */}
                {selectedRole.inheritsFrom &&
                  selectedRole.inheritsFrom.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <GitBranch className="h-4 w-4" />
                          Inheritance
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {selectedRole.inheritsFrom.map((parentId) => {
                            const parentRole = roles.find(
                              (r: RoleDefinition) => r.id === parentId,
                            );
                            return parentRole ? (
                              <div
                                key={parentId}
                                className="flex items-center gap-2"
                              >
                                <Shield className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">
                                  {parentRole.name}
                                </span>
                                <Badge variant="outline" className="text-xs">
                                  {parentRole.permissions.length} permissions
                                </Badge>
                              </div>
                            ) : null;
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                {/* Conflicts Section */}
                {conflicts.length > 0 && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Permission Conflicts Detected</AlertTitle>
                    <AlertDescription>
                      <div className="mt-2 space-y-2">
                        {conflicts.map((conflict, index) => (
                          <div key={index} className="text-sm">
                            <p>
                              {conflict.type} conflict with severity:{" "}
                              {conflict.severity}
                            </p>
                            <Button
                              size="sm"
                              variant="outline"
                              className="mt-1"
                              onClick={() => setShowConflictDialog(true)}
                            >
                              Resolve Conflict
                            </Button>
                          </div>
                        ))}
                      </div>
                    </AlertDescription>
                  </Alert>
                )}

                {/* Permissions Section */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      Permissions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {Object.values(ResourceType).map((resource) => {
                        const resourcePermissions =
                          selectedRole.permissions.filter(
                            (p) => p.resource === resource,
                          );

                        if (resourcePermissions.length === 0) return null;

                        return (
                          <div key={resource}>
                            <div className="font-medium text-sm mb-2">
                              {resource}
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {resourcePermissions.map((perm) => (
                                <Badge key={perm.id} variant="secondary">
                                  {perm.action}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Metadata Section */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Metadata</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">
                          Created By:
                        </span>
                        <p className="font-medium">{selectedRole.createdBy}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">
                          Created At:
                        </span>
                        <p className="font-medium">
                          {new Date(
                            selectedRole.createdAt,
                          ).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">
                          Updated By:
                        </span>
                        <p className="font-medium">{selectedRole.updatedBy}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">
                          Updated At:
                        </span>
                        <p className="font-medium">
                          {new Date(
                            selectedRole.updatedAt,
                          ).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </ScrollArea>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <Shield className="h-12 w-12 mx-auto mb-4" />
              <p>Select a role to view details</p>
            </div>
          </div>
        )}
      </div>

      {/* Dialogs */}
      <CreateRoleDialog />
    </div>
  );
};
